import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Skeleton,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Block as RevokeIcon,
  PhonelinkLock as DeviceIcon,
  People,
  Refresh as RefreshIcon,
  School,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { usePermissions } from "../../hooks/usePermissions";
import deviceAuthorizationService from "../../services/deviceAuthorizationService";
import classService from "../../services/classService";
import departmentService from "../../services/departmentService";
import {
  DEVICE_STUDENT_READ_PERMS,
  DEVICE_STAFF_READ_PERMS,
  DEVICE_STUDENT_APPROVE_PERMS,
  DEVICE_STAFF_APPROVE_PERMS,
  DEVICE_STUDENT_REJECT_PERMS,
  DEVICE_STAFF_REJECT_PERMS,
} from "../../config/deviceApprovalPermissions";

const extractDeviceList = (response) => {
  if (!response) return [];
  const candidates = [response, response?.data, response?.data?.data];
  for (const source of candidates) {
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
  }
  return [];
};

/**
 * Read the backend total from the paginated response. Falls back to the current
 * page's row count when the backend didn't return a total (older builds), so the
 * UI never shows a wrong "0 of N".
 */
const extractDeviceTotal = (response, fallbackRows) => {
  const body = response?.data ?? response;
  const total = body?.total ?? body?.data?.total;
  if (total != null && Number.isFinite(Number(total))) return Number(total);
  return Array.isArray(fallbackRows) ? fallbackRows.length : 0;
};

const statusColor = (status) => {
  switch (status) {
    case "APPROVED":
      return "success";
    case "PENDING_APPROVAL":
      return "warning";
    case "REJECTED":
      return "error";
    case "REVOKED":
      return "default";
    default:
      return "default";
  }
};

const formatStatusLabel = (status) =>
  status ? String(status).replace(/_/g, " ") : "—";

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DetailRow = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word", mt: 0.25 }}>
      {value ?? "—"}
    </Typography>
  </Grid>
);

const SKELETON_ROW_COUNT = 6;

const DeviceTableSkeleton = () => (
  <TableContainer>
    <Table size="small" sx={{ tableLayout: "fixed", minWidth: 720 }}>
      <TableHead>
        <TableRow>
          {["28%", "22%", "14%", "22%", "14%"].map((width) => (
            <TableCell key={width} sx={{ width }}>
              <Skeleton variant="text" width="60%" height={24} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
          <TableRow key={i}>
            <TableCell>
              <Skeleton variant="text" width="85%" height={22} />
              <Skeleton variant="text" width="55%" height={18} sx={{ mt: 0.5 }} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width="75%" height={22} />
              <Skeleton variant="text" width="45%" height={18} sx={{ mt: 0.5 }} />
            </TableCell>
            <TableCell>
              <Skeleton variant="rounded" width={96} height={26} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width="90%" height={22} />
            </TableCell>
            <TableCell align="right">
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const reviewSummary = (row) => {
  if (!row) return "—";
  if (row.status === "APPROVED") {
    return row.approvedByName
      ? `Approved by ${row.approvedByName}`
      : "Approved";
  }
  if (row.status === "REJECTED" || row.status === "REVOKED") {
    const who = row.rejectedByName;
    const verb = row.status === "REVOKED" ? "Revoked" : "Rejected";
    return who ? `${verb} by ${who}` : verb;
  }
  return "Awaiting review";
};

const DeviceApprovals = () => {
  const { user } = useSelector((state) => state.auth);
  const { selectedInstitute } = useSelector((state) => state.instituteContext);
  const { hasAnyPermissionRaw, isLoading: permissionsLoading } = usePermissions();
  const rawRole = user?.role?.slug ?? user?.role ?? "";
  const roleSlug = typeof rawRole === "string" ? rawRole.toLowerCase() : "";

  const canViewStudent = hasAnyPermissionRaw(DEVICE_STUDENT_READ_PERMS);
  const canViewStaff = hasAnyPermissionRaw(DEVICE_STAFF_READ_PERMS);
  const canApproveStudent = hasAnyPermissionRaw(DEVICE_STUDENT_APPROVE_PERMS);
  const canApproveStaff = hasAnyPermissionRaw(DEVICE_STAFF_APPROVE_PERMS);
  const canRejectStudent = hasAnyPermissionRaw(DEVICE_STUDENT_REJECT_PERMS);
  const canRejectStaff = hasAnyPermissionRaw(DEVICE_STAFF_REJECT_PERMS);
  const canReviewStudent = canViewStudent;
  const canReviewStaff = canViewStaff;

  const [mainTab, setMainTab] = useState("student");
  const [listTab, setListTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** @type {{ rowId: number, action: 'approve'|'reject'|'revoke' } | null} */
  const [actionLoading, setActionLoading] = useState(null);
  const actionLoadingId = actionLoading?.rowId ?? null;
  const setActionLoadingId = (id) => {
    if (id == null) setActionLoading(null);
    else
      setActionLoading((cur) => ({
        rowId: id,
        action: cur?.action ?? "approve",
      }));
  };
  const [viewRow, setViewRow] = useState(null);
  const [approveDialog, setApproveDialog] = useState({ open: false, row: null });
  const [approveAllDialog, setApproveAllDialog] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, row: null });
  const [rejectReason, setRejectReason] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [requestedFrom, setRequestedFrom] = useState("");
  const [requestedTo, setRequestedTo] = useState("");
  const [filterClassId, setFilterClassId] = useState("all");
  const [filterSection, setFilterSection] = useState("all");
  const [filterBatchCode, setFilterBatchCode] = useState("all");
  const [filterDepartmentId, setFilterDepartmentId] = useState("all");
  const [classOptions, setClassOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [page, setPage] = useState(0); // 0-based for MUI TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const instituteId = useMemo(() => {
    if (user?.instituteId != null && user.instituteId !== "") {
      return Number(user.instituteId);
    }
    if (selectedInstitute?.id != null) return Number(selectedInstitute.id);
    return null;
  }, [user?.instituteId, selectedInstitute?.id]);

  const rolePath = `/${roleSlug === "institute" ? "admin" : roleSlug || "admin"}`;

  useEffect(() => {
    // Default tab is driven purely by permissions (RBAC), not by role. A teacher granted
    // staff device permissions should land on (and be able to use) the staff tab.
    if (canReviewStudent && !canReviewStaff) setMainTab("student");
    else if (canReviewStaff && !canReviewStudent) setMainTab("staff");
    else setMainTab("student");
  }, [canReviewStudent, canReviewStaff]);

  const canActOnCurrentTab =
    mainTab === "student" ? canViewStudent : canViewStaff;
  const canApproveOnTab =
    mainTab === "student" ? canApproveStudent : canApproveStaff;
  const canRejectOnTab = mainTab === "student" ? canRejectStudent : canRejectStaff;

  useEffect(() => {
    if (mainTab !== "student" || !instituteId) {
      setClassOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await classService.getAllClasses({ page: 1, limit: 500, instituteId });
        const rows = res?.data?.data?.rows ?? res?.data?.rows ?? res?.data ?? [];
        if (!cancelled) setClassOptions(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setClassOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mainTab, instituteId]);

  // Department options for the student Department filter.
  useEffect(() => {
    if (mainTab !== "student" || !instituteId) {
      setDepartmentOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await departmentService.getDepartmentNames(instituteId);
        const rows = res?.data?.data ?? res?.data ?? [];
        if (!cancelled) setDepartmentOptions(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setDepartmentOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mainTab, instituteId]);

  const sectionOptions = useMemo(() => {
    const sections = new Set();
    for (const cls of classOptions) {
      const section = cls?.section || cls?.classSection || cls?.sectionName;
      if (section) sections.add(String(section));
    }
    return [...sections].sort();
  }, [classOptions]);

  const batchOptions = useMemo(() => {
    if (!filterClassId || filterClassId === "all") return [];
    const selected = classOptions.find((c) => String(c.id) === String(filterClassId));
    const batches = selected?.practicalBatches || selected?.batches || [];
    return Array.isArray(batches) ? batches.map((b) => String(b).toUpperCase()) : [];
  }, [classOptions, filterClassId]);

  const loadRows = useCallback(async () => {
    if (permissionsLoading) return;
    if (!canActOnCurrentTab) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = {
        subjectType: mainTab,
        page: page + 1, // backend is 1-based
        limit: rowsPerPage,
        ...(instituteId ? { instituteId } : {}),
        ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
        ...(filterPlatform !== "all" ? { platform: filterPlatform } : {}),
        ...(requestedFrom ? { requestedFrom } : {}),
        ...(requestedTo ? { requestedTo } : {}),
        ...(mainTab === "student" && filterDepartmentId !== "all" ? { departmentId: filterDepartmentId } : {}),
        ...(mainTab === "student" && filterClassId !== "all" ? { classId: filterClassId } : {}),
        ...(mainTab === "student" && filterSection !== "all" ? { section: filterSection } : {}),
        ...(mainTab === "student" && filterBatchCode !== "all" ? { practicalBatchCode: filterBatchCode } : {}),
      };
      const res =
        listTab === 0
          ? await deviceAuthorizationService.getPending(params)
          : await deviceAuthorizationService.getDevices({
              ...params,
              ...(filterStatus !== "all" ? { status: filterStatus } : {}),
            });
      const list = extractDeviceList(res);
      setRows(list);
      setTotal(extractDeviceTotal(res, list));
    } catch (e) {
      console.error(e);
      setRows([]);
      setTotal(0);
      setError(e?.response?.data?.message || "Failed to load device requests");
    } finally {
      setLoading(false);
    }
  }, [mainTab, listTab, instituteId, canActOnCurrentTab, permissionsLoading, searchQuery, filterPlatform, filterStatus, requestedFrom, requestedTo, filterDepartmentId, filterClassId, filterSection, filterBatchCode, page, rowsPerPage]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  // Reset to the first page whenever a filter or tab changes so the user never
  // lands on an out-of-range page after narrowing the result set.
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab, listTab, searchQuery, filterPlatform, filterStatus, requestedFrom, requestedTo, filterDepartmentId, filterClassId, filterSection, filterBatchCode]);

  const stats = useMemo(() => {
    const pending = rows.filter((r) => r.status === "PENDING_APPROVAL").length;
    const approved = rows.filter((r) => r.status === "APPROVED").length;
    const rejected = rows.filter((r) =>
      ["REJECTED", "REVOKED"].includes(r.status)
    ).length;
    return { pending, approved, rejected };
  }, [rows]);

  const pendingRows = useMemo(
    () => rows.filter((r) => r?.status === "PENDING_APPROVAL"),
    [rows]
  );

  const isRowBusy = (rowId) => actionLoading?.rowId === rowId;
  const isActionBusy = (rowId, action) =>
    actionLoading?.rowId === rowId && actionLoading?.action === action;

  const approveAllPending = async () => {
    const targets = pendingRows.slice();
    if (targets.length === 0) {
      setApproveAllDialog(false);
      return;
    }
    setBulkApproving(true);
    let succeeded = 0;
    let failed = 0;
    for (const row of targets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await deviceAuthorizationService.approve(row.id);
        succeeded += 1;
      } catch (e) {
        failed += 1;
        console.error("[approveAllPending] Failed for row", row?.id, e?.message || e);
      }
    }
    setBulkApproving(false);
    setApproveAllDialog(false);
    const message =
      failed === 0
        ? `Approved ${succeeded} device request${succeeded === 1 ? "" : "s"}.`
        : `Approved ${succeeded}, failed ${failed}. Retry the remaining requests.`;
    setSnack({ open: true, message, severity: failed === 0 ? "success" : "warning" });
    try {
      await loadRows();
    } catch (e) {
      console.error("[approveAllPending] reload failed:", e?.message || e);
    }
  };

  const runAction = async (fn, successMessage, rowId, { action, onSuccess } = {}) => {
    try {
      const res = await fn();
      const body = res?.data;
      const updated = body?.data ?? (body?.id != null ? body : null);
      if (updated?.id != null && viewRow?.id === rowId) {
        setViewRow(updated);
      }
      setSnack({ open: true, message: successMessage, severity: "success" });
      onSuccess?.();
      await loadRows();
    } catch (e) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || "Action failed",
        severity: "error",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const startAction = (rowId, action) => setActionLoading({ rowId, action });

  const renderRowActions = (row) => {
    const busy = isRowBusy(row.id);
    const showApprove = row.status === "PENDING_APPROVAL" && canApproveOnTab;
    const showReject = row.status === "PENDING_APPROVAL" && canRejectOnTab;
    const showRevoke =
      row.status === "APPROVED" && listTab === 1 && canApproveOnTab;
    return (
      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
        <Tooltip title="View details">
          <span>
            <IconButton
              size="small"
              disabled={busy}
              onClick={() => setViewRow(row)}
              aria-label="View details"
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      {(showApprove || showReject) && (
        <>
          {showApprove && (
          <Tooltip title="Approve">
              <span>
                <IconButton
                  size="small"
                  color="success"
                  disabled={busy}
                  aria-label="Approve"
                  onClick={() => setApproveDialog({ open: true, row })}
                >
                  {isActionBusy(row.id, "approve") ? (
                    <CircularProgress size={18} color="success" />
                  ) : (
                    <ApproveIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showReject && (
          <Tooltip title="Reject">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={busy}
                  aria-label="Reject"
                  onClick={() => setRejectDialog({ open: true, row })}
                >
                  {isActionBusy(row.id, "reject") ? (
                    <CircularProgress size={18} color="error" />
                  ) : (
                    <RejectIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </>
      )}
      {showRevoke && (
          <Tooltip title="Revoke access">
            <span>
              <IconButton
                size="small"
                color="warning"
                disabled={busy}
                aria-label="Revoke"
                onClick={() => {
                  startAction(row.id, "revoke");
                  void runAction(
                    () => deviceAuthorizationService.revoke(row.id),
                    "Device access revoked.",
                    row.id,
                    { action: "revoke" }
                  );
                }}
              >
                {isActionBusy(row.id, "revoke") ? (
                  <CircularProgress size={18} color="warning" />
                ) : (
                  <RevokeIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    );
  };

  if (permissionsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canReviewStudent && !canReviewStaff) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You do not have permission to view device login requests. Ask your institute
          administrator to grant device permissions under the <strong>Device</strong> module in
          RBAC (view / approve / reject for students or staff), or{" "}
          <strong>Update Student Profile</strong> / <strong>Update Teacher Profile</strong> (same as
          profile edit requests).
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <DeviceIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Device login approvals
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <ToggleButtonGroup
            value={mainTab}
            exclusive
            onChange={(_e, value) => value && setMainTab(value)}
            size="small"
            sx={{
              "& .MuiToggleButton-root": { textTransform: "none", px: 2 },
              "& .Mui-selected": {
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
              },
            }}
          >
            {canReviewStudent && (
              <ToggleButton value="student">
                <School sx={{ mr: 0.75, fontSize: 18 }} /> Student
              </ToggleButton>
            )}
            {canReviewStaff && (
              <ToggleButton value="staff">
                <People sx={{ mr: 0.75, fontSize: 18 }} /> Staff
              </ToggleButton>
            )}
          </ToggleButtonGroup>
          {listTab === 0 && canApproveOnTab && pendingRows.length > 0 && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={
                bulkApproving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ApproveIcon />
                )
              }
              disabled={loading || bulkApproving || actionLoadingId != null}
              onClick={() => setApproveAllDialog(true)}
            >
              Approve all ({pendingRows.length})
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
            }
            disabled={loading || bulkApproving || actionLoadingId != null}
            onClick={() => void loadRows()}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        {mainTab === "student"
          ? "Student device login requests — open View for full device and review details."
          : "Staff device login requests — open View for full device and review details."}
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total (matching filters)", value: total, color: "primary.main" },
          { label: "Pending (this page)", value: stats.pending, color: "warning.main" },
          { label: "Approved (this page)", value: stats.approved, color: "success.main" },
          { label: "Rejected / Revoked (this page)", value: stats.rejected, color: "error.main" },
        ].map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.label}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: stat.color }}>
                  {loading ? (
                    <Skeleton variant="text" width={48} sx={{ mx: "auto", fontSize: "2.125rem" }} />
                  ) : (
                    stat.value
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="Search user/device"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            select
            label="Platform"
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 120 }}
          >
            <option value="all">All</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
            <option value="web">Web</option>
          </TextField>
          {listTab === 1 ? (
            <TextField
              size="small"
              select
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ minWidth: 160 }}
            >
              <option value="all">All</option>
              <option value="PENDING_APPROVAL">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="REVOKED">Revoked</option>
            </TextField>
          ) : null}
          <TextField
            size="small"
            type="date"
            label="Requested from"
            InputLabelProps={{ shrink: true }}
            value={requestedFrom}
            onChange={(e) => setRequestedFrom(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="Requested to"
            InputLabelProps={{ shrink: true }}
            value={requestedTo}
            onChange={(e) => setRequestedTo(e.target.value)}
          />
          {mainTab === "student" ? (
            <>
              <TextField
                size="small"
                select
                label="Department"
                value={filterDepartmentId}
                onChange={(e) => setFilterDepartmentId(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: 180 }}
              >
                <option value="all">All departments</option>
                {departmentOptions.map((dept) => (
                  <option key={dept.id ?? dept.value} value={dept.id ?? dept.value}>
                    {dept.name ?? dept.label ?? dept.departmentName ?? `#${dept.id}`}
                  </option>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Class"
                value={filterClassId}
                onChange={(e) => {
                  setFilterClassId(e.target.value);
                  setFilterBatchCode("all");
                }}
                SelectProps={{ native: true }}
                sx={{ minWidth: 160 }}
              >
                <option value="all">All classes</option>
                {classOptions.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.className || cls.name}
                    {cls.section ? ` - ${cls.section}` : ""}
                  </option>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Section"
                value={filterSection}
                onChange={(e) => setFilterSection(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: 130 }}
              >
                <option value="all">All sections</option>
                {sectionOptions.map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Batch"
                value={filterBatchCode}
                onChange={(e) => setFilterBatchCode(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: 130 }}
                disabled={filterClassId === "all" || batchOptions.length === 0}
              >
                <option value="all">All batches</option>
                {batchOptions.map((batch) => (
                  <option key={batch} value={batch}>
                    {batch}
                  </option>
                ))}
              </TextField>
            </>
          ) : null}
          <Button size="small" variant="contained" onClick={() => void loadRows()}>
            Apply
          </Button>
        </Box>
        <Tabs
          value={listTab}
          onChange={(_, v) => !loading && setListTab(v)}
          sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Pending approval" />
          <Tab label="All devices" />
        </Tabs>
        <CardContent sx={{ pt: 2 }}>
          {!canActOnCurrentTab ? (
            <Alert severity="warning">You cannot review {mainTab} device requests.</Alert>
          ) : loading ? (
            <DeviceTableSkeleton />
          ) : (
            <TableContainer>
              <Table size="small" sx={{ tableLayout: "fixed", minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: "28%" }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "22%" }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "14%" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: "22%" }}>Audit log</TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, width: "14%", whiteSpace: "nowrap" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} hover sx={{ "& td": { verticalAlign: "middle" } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} noWrap title={row.userName}>
                          {row.userName || `User #${row.userId}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {row.userEmail || row.userRole || ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={row.deviceName || row.deviceId}>
                          {row.deviceName || "Unknown device"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {row.platform || "—"}
                          {row.appVersion ? ` · v${row.appVersion}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={formatStatusLabel(row.status)}
                          color={statusColor(row.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap title={reviewSummary(row)}>
                          {reviewSummary(row)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Requested: {formatDateTime(row.requestedAt || row.createdAt)}
                        </Typography>
                        {row.approvedAt ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Approved: {formatDateTime(row.approvedAt)}
                            {row.approvedByName ? ` by ${row.approvedByName}` : ""}
                          </Typography>
                        ) : null}
                        {row.rejectedAt ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {row.status === "REVOKED" ? "Revoked" : "Rejected"}: {formatDateTime(row.rejectedAt)}
                            {row.rejectedByName ? ` by ${row.rejectedByName}` : ""}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {renderRowActions(row)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5, color: "text.secondary" }}>
                        {listTab === 0
                          ? `No pending ${mainTab} device login requests.`
                          : `No ${mainTab} devices in this list.`}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Per page:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} of ${count}`
                }
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* View details */}
      <Dialog
        open={Boolean(viewRow)}
        onClose={() => setViewRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Device login request
          {viewRow && (
            <Chip
              size="small"
              label={formatStatusLabel(viewRow.status)}
              color={statusColor(viewRow.status)}
              variant="outlined"
              sx={{ ml: 1.5, verticalAlign: "middle" }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {viewRow && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                User
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <DetailRow label="Name" value={viewRow.userName} />
                <DetailRow label="Email" value={viewRow.userEmail} />
                <DetailRow label="Role" value={viewRow.userRole} />
                <DetailRow label="User ID" value={viewRow.userId} />
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Device
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <DetailRow label="Device name" value={viewRow.deviceName} />
                <DetailRow label="Platform" value={viewRow.platform} />
                <DetailRow label="App version" value={viewRow.appVersion} />
                <DetailRow label="Device ID" value={viewRow.deviceId} />
                <DetailRow label="IP address" value={viewRow.ipAddress} />
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Timeline
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <DetailRow label="Requested at" value={formatDateTime(viewRow.createdAt)} />
                <DetailRow label="Last updated" value={formatDateTime(viewRow.updatedAt)} />
                <DetailRow label="Last login on device" value={formatDateTime(viewRow.lastLoginAt)} />
              </Grid>

              {(viewRow.status === "APPROVED" ||
                viewRow.status === "REJECTED" ||
                viewRow.status === "REVOKED") && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Review decision
                  </Typography>
                  <Grid container spacing={2}>
                    {viewRow.status === "APPROVED" && (
                      <>
                        <DetailRow label="Approved by" value={viewRow.approvedByName} />
                        <DetailRow label="Approved at" value={formatDateTime(viewRow.approvedAt)} />
                      </>
                    )}
                    {(viewRow.status === "REJECTED" || viewRow.status === "REVOKED") && (
                      <>
                        <DetailRow
                          label={viewRow.status === "REVOKED" ? "Revoked by" : "Rejected by"}
                          value={viewRow.rejectedByName}
                        />
                        <DetailRow
                          label={viewRow.status === "REVOKED" ? "Revoked at" : "Rejected at"}
                          value={formatDateTime(viewRow.rejectedAt)}
                        />
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Reason
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {viewRow.rejectionReason || "—"}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Button onClick={() => setViewRow(null)}>Close</Button>
          {viewRow?.status === "PENDING_APPROVAL" && (canApproveOnTab || canRejectOnTab) && (
            <Stack direction="row" spacing={1}>
              {canRejectOnTab && (
              <Button
                color="error"
                variant="outlined"
                startIcon={
                  isActionBusy(viewRow.id, "reject") ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <RejectIcon />
                  )
                }
                disabled={isRowBusy(viewRow.id)}
                onClick={() => setRejectDialog({ open: true, row: viewRow })}
              >
                Reject
              </Button>
              )}
              {canApproveOnTab && (
              <Button
                color="success"
                variant="contained"
                startIcon={
                  isActionBusy(viewRow.id, "approve") ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <ApproveIcon />
                  )
                }
                disabled={isRowBusy(viewRow.id)}
                onClick={() => setApproveDialog({ open: true, row: viewRow })}
              >
                Approve
              </Button>
              )}
            </Stack>
          )}
          {viewRow?.status === "APPROVED" && listTab === 1 && canApproveOnTab && (
            <Button
              color="warning"
              variant="outlined"
              startIcon={
                isActionBusy(viewRow.id, "revoke") ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RevokeIcon />
                )
              }
              disabled={isRowBusy(viewRow.id)}
              onClick={() => {
                startAction(viewRow.id, "revoke");
                void runAction(
                  () => deviceAuthorizationService.revoke(viewRow.id),
                  "Device access revoked.",
                  viewRow.id,
                  { action: "revoke" }
                );
              }}
            >
              Revoke access
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={approveAllDialog}
        onClose={() => {
          if (bulkApproving) return;
          setApproveAllDialog(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve all pending requests?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Approve all <strong>{pendingRows.length}</strong> pending {mainTab} device login
            request{pendingRows.length === 1 ? "" : "s"} currently in this list?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Each user will be notified and can sign in on the requested device. Any other approved
            device for that user will be logged out and must request approval again. Filters you
            have applied are respected, so only the visible pending requests are approved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={bulkApproving} onClick={() => setApproveAllDialog(false)}>
            Cancel
          </Button>
          <Button
            color="success"
            variant="contained"
            startIcon={
              bulkApproving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <ApproveIcon />
              )
            }
            disabled={bulkApproving || pendingRows.length === 0}
            onClick={() => void approveAllPending()}
          >
            {bulkApproving ? "Approving…" : `Approve all (${pendingRows.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approveDialog.open}
        onClose={() => {
          if (isRowBusy(approveDialog.row?.id)) return;
          setApproveDialog({ open: false, row: null });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve device request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Allow <strong>{approveDialog.row?.userName}</strong> to sign in from{" "}
            <strong>{approveDialog.row?.deviceName || approveDialog.row?.deviceId}</strong> (
            {approveDialog.row?.platform || "unknown platform"})?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            The user will be notified and can sign in on this device. Any other approved device
            will be logged out and must request approval again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isRowBusy(approveDialog.row?.id)}
            onClick={() => setApproveDialog({ open: false, row: null })}
          >
            Cancel
          </Button>
          <Button
            color="success"
            variant="contained"
            startIcon={
              isActionBusy(approveDialog.row?.id, "approve") ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <ApproveIcon />
              )
            }
            disabled={
              !approveDialog.row || isActionBusy(approveDialog.row?.id, "approve")
            }
            onClick={() => {
              const row = approveDialog.row;
              if (!row || isRowBusy(row.id)) return;
              startAction(row.id, "approve");
              void runAction(
                () => deviceAuthorizationService.approve(row.id),
                "Device approved. User can sign in now.",
                row.id,
                {
                  action: "approve",
                  onSuccess: () => setApproveDialog({ open: false, row: null }),
                }
              );
            }}
          >
            {isActionBusy(approveDialog.row?.id, "approve") ? "Approving…" : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialog.open}
        onClose={() => {
          if (isRowBusy(rejectDialog.row?.id)) return;
          setRejectDialog({ open: false, row: null });
          setRejectReason("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject device request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reject login from{" "}
            <strong>{rejectDialog.row?.deviceName || rejectDialog.row?.deviceId}</strong> for{" "}
            <strong>{rejectDialog.row?.userName}</strong>?
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            value={rejectReason}
            disabled={isRowBusy(rejectDialog.row?.id)}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isRowBusy(rejectDialog.row?.id)}
            onClick={() => {
              setRejectDialog({ open: false, row: null });
              setRejectReason("");
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={
              isActionBusy(rejectDialog.row?.id, "reject") ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <RejectIcon />
              )
            }
            disabled={
              !rejectDialog.row || isActionBusy(rejectDialog.row?.id, "reject")
            }
            onClick={() => {
              const row = rejectDialog.row;
              if (!row || isRowBusy(row.id)) return;
              startAction(row.id, "reject");
              void runAction(
                () => deviceAuthorizationService.reject(row.id, { reason: rejectReason }),
                "Device request rejected.",
                row.id,
                {
                  action: "reject",
                  onSuccess: () => {
                    setRejectDialog({ open: false, row: null });
                    setRejectReason("");
                  },
                }
              );
            }}
          >
            {isActionBusy(rejectDialog.row?.id, "reject") ? "Rejecting…" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%", minWidth: 280, boxShadow: 3 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeviceApprovals;
